// Settings view for a project. Four sections, single scrollable page:
//   - General: rename, default branch
//   - Repository: link a GitHub installation + repo + branch
//   - Webhooks: list + add/edit/delete + view-log
//   - Danger zone: delete project (type-to-confirm)

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Menu } from "@base-ui-components/react/menu";
import {
	AlertTriangle,
	Check,
	Copy,
	ExternalLink,
	Github,
	Link2,
	Link2Off,
	Loader2,
	MoreHorizontal,
	Pencil,
	Plus,
	Power,
	RefreshCw,
	Trash2,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { GlassPanel, Pill } from "@/components/glass";
import { GlassSelect, GlassSelectItem } from "@/components/glass-select";
import { Switch } from "@/components/switch";
import { WebhookCreateDialog } from "@/components/webhook-create-dialog";
import { WebhookEditDialog } from "@/components/webhook-edit-dialog";
import { WebhookEventsDrawer } from "@/components/webhook-events-drawer";

interface SyncSlices {
	docs: boolean;
	code: boolean;
	commits: boolean;
	prs: boolean;
	issues: boolean;
	releases: boolean;
}

interface SyncConfig {
	slices: SyncSlices;
	branch?: string;
	includeBots?: boolean;
	prReviews?: boolean;
}

const DEFAULT_SYNC_CONFIG: SyncConfig = {
	slices: {
		docs: true,
		code: false,
		commits: true,
		prs: false,
		issues: false,
		releases: true,
	},
};

interface Project {
	id: string;
	name: string;
	defaultBranch: string;
	githubRepo: string | null;
	githubInstallationId: string | null;
	lastBaselineSyncAt: string | number | null;
	syncConfig: SyncConfig | null;
	createdAt: string;
}

interface Webhook {
	id: string;
	projectId: string;
	name: string;
	slug: string;
	secret: string | null;
	sourceName: string;
	mode: string;
	enabled: boolean;
	requestCount: number;
	ingestCount: number;
	lastReceivedAt: string | null;
	lastStatus: string | null;
	autoDisabledAt: string | null;
	autoDisabledReason: string | null;
	createdAt: string;
	updatedAt: string;
}

async function fetchProject(id: string): Promise<Project> {
	const r = await fetch(`/api/projects/${id}`, { credentials: "include" });
	if (!r.ok) throw new Error(`project: ${r.status}`);
	const body = (await r.json()) as { project: Project };
	return body.project;
}

async function fetchWebhooks(projectId: string): Promise<Webhook[]> {
	const r = await fetch(`/api/projects/${projectId}/webhooks`, {
		credentials: "include",
	});
	if (!r.ok) throw new Error(`webhooks: ${r.status}`);
	return ((await r.json()) as { webhooks: Webhook[] }).webhooks;
}

export default function SettingsView() {
	const { id: projectId } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const qc = useQueryClient();

	const projectQ = useQuery({
		queryKey: ["project", projectId],
		queryFn: () => fetchProject(projectId!),
		enabled: !!projectId,
	});

	const webhooksQ = useQuery({
		queryKey: ["webhooks", projectId],
		queryFn: () => fetchWebhooks(projectId!),
		enabled: !!projectId,
	});

	// Local form state for General. Initialized from server fetch.
	const [name, setName] = useState("");
	useEffect(() => {
		if (projectQ.data) {
			setName(projectQ.data.name);
		}
	}, [projectQ.data]);

	const saveGeneral = useMutation({
		mutationFn: async (body: { name?: string; defaultBranch?: string }) => {
			const r = await fetch(`/api/projects/${projectId}`, {
				method: "PATCH",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(body),
			});
			if (!r.ok) throw new Error(`save: ${r.status}`);
			return r.json();
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["project", projectId] });
			qc.invalidateQueries({ queryKey: ["projects"] });
		},
	});

	const deleteProject = useMutation({
		mutationFn: async () => {
			const r = await fetch(`/api/projects/${projectId}`, {
				method: "DELETE",
				credentials: "include",
			});
			if (!r.ok) throw new Error(`delete: ${r.status}`);
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["projects"] });
			navigate("/");
		},
	});

	const [deleteOpen, setDeleteOpen] = useState(false);
	const [createOpen, setCreateOpen] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [eventsForId, setEventsForId] = useState<string | null>(null);

	const project = projectQ.data;
	const webhooks = webhooksQ.data ?? [];
	const dirtyGeneral = !!project && name !== project.name;

	return (
		<div className="h-full overflow-y-auto pt-1 pb-8">
			<div className="max-w-[800px] mx-auto space-y-4">
				<h1 className="text-[18px] font-medium tracking-tight mt-2 mb-4">
					Project settings
				</h1>

				{/* GENERAL ------------------------------------------------ */}
				<GlassPanel className="p-5 space-y-4">
					<SectionHeader title="General" subtitle="Rename this project." />
					<Field label="Name">
						<input
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							maxLength={100}
							className="w-full h-9 px-3 rounded-[var(--radius-pill)] bg-[var(--color-surface-solid)] border border-[var(--color-border)] text-[13px] outline-none focus:border-[var(--color-border-hi)]"
						/>
					</Field>
					<div className="flex justify-end">
						<Pill
							onClick={() => saveGeneral.mutate({ name })}
							disabled={!dirtyGeneral || saveGeneral.isPending}
						>
							{saveGeneral.isPending ? (
								<Loader2 className="w-3.5 h-3.5 animate-spin" />
							) : (
								<Check className="w-3.5 h-3.5" />
							)}
							Save
						</Pill>
					</div>
				</GlassPanel>

				{/* REPOSITORY --------------------------------------------- */}
				{projectId && project && (
					<RepositorySection project={project} projectId={projectId} />
				)}

				{/* WEBHOOKS ----------------------------------------------- */}
				<GlassPanel className="p-5 space-y-4">
					<div className="flex items-center justify-between">
						<SectionHeader
							title="Webhooks"
							subtitle="Inbound endpoints — external systems POST JSON, we ingest into the graph."
						/>
						<Pill onClick={() => setCreateOpen(true)}>
							<Plus className="w-3.5 h-3.5" />
							Add webhook
						</Pill>
					</div>
					{webhooks.length === 0 && !webhooksQ.isLoading && (
						<div className="text-[13px] text-[var(--color-text-muted)] py-4 text-center">
							No webhooks yet. Add one to start receiving data.
						</div>
					)}
					<div className="space-y-2">
						{webhooks.map((wh) => (
							<WebhookRow
								key={wh.id}
								webhook={wh}
								onEdit={() => setEditingId(wh.id)}
								onViewEvents={() => setEventsForId(wh.id)}
							/>
						))}
					</div>
				</GlassPanel>

				{/* DANGER ZONE -------------------------------------------- */}
				<GlassPanel className="p-5 space-y-4" style={{ borderColor: "rgba(239,68,68,0.2)" }}>
					<SectionHeader
						title="Danger zone"
						subtitle="Destructive actions. No undo."
						danger
					/>
					<div className="flex items-center justify-between gap-4 py-2">
						<div>
							<div className="text-[13px]">Delete this project</div>
							<div className="text-[12px] text-[var(--color-text-muted)]">
								Permanently removes the project, all of its memory data, jobs, and webhooks.
							</div>
						</div>
						<Pill
							onClick={() => setDeleteOpen(true)}
							className="!text-[var(--color-danger)] !border-[rgba(239,68,68,0.35)]"
						>
							<Trash2 className="w-3.5 h-3.5" />
							Delete
						</Pill>
					</div>
				</GlassPanel>
			</div>

			{/* Dialogs --------------------------------------------------- */}
			{project && (
				<ConfirmDialog
					open={deleteOpen}
					onOpenChange={setDeleteOpen}
					title="Delete project?"
					body={
						<>
							This permanently removes <span className="mono text-[var(--color-text)]">{project.name}</span>{" "}
							and all of its memory data. There is no undo.
						</>
					}
					confirmText={project.name}
					confirmLabel="Delete project"
					tone="danger"
					onConfirm={() => deleteProject.mutateAsync()}
				/>
			)}

			{projectId && (
				<WebhookCreateDialog
					open={createOpen}
					onOpenChange={setCreateOpen}
					projectId={projectId}
				/>
			)}

			{editingId && projectId && (
				<WebhookEditDialog
					open={!!editingId}
					onOpenChange={(o) => !o && setEditingId(null)}
					projectId={projectId}
					webhookId={editingId}
				/>
			)}

			{eventsForId && projectId && (
				<WebhookEventsDrawer
					open={!!eventsForId}
					onOpenChange={(o) => !o && setEventsForId(null)}
					projectId={projectId}
					webhookId={eventsForId}
				/>
			)}
		</div>
	);
}

function SectionHeader({
	title,
	subtitle,
	danger,
}: {
	title: string;
	subtitle?: string;
	danger?: boolean;
}) {
	return (
		<div>
			<div className={`text-[14px] font-medium flex items-center gap-2 ${danger ? "text-[var(--color-danger)]" : ""}`}>
				{danger && <AlertTriangle className="w-3.5 h-3.5" />}
				{title}
			</div>
			{subtitle && (
				<div className="text-[12px] text-[var(--color-text-muted)] mt-0.5">
					{subtitle}
				</div>
			)}
		</div>
	);
}

function Field({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div>
			<label className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] block mb-1">
				{label}
			</label>
			{children}
		</div>
	);
}

interface WebhookRowProps {
	webhook: Webhook;
	onEdit: () => void;
	onViewEvents: () => void;
}

function WebhookRow({ webhook, onEdit, onViewEvents }: WebhookRowProps) {
	const url = `${window.location.origin}/api/hooks/${webhook.slug}`;
	const [copied, setCopied] = useState(false);
	const qc = useQueryClient();

	const remove = useMutation({
		mutationFn: async () => {
			const r = await fetch(
				`/api/projects/${webhook.projectId}/webhooks/${webhook.id}`,
				{ method: "DELETE", credentials: "include" },
			);
			if (!r.ok) throw new Error(`delete: ${r.status}`);
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["webhooks", webhook.projectId] }),
	});

	const toggleEnabled = useMutation({
		mutationFn: async () => {
			const r = await fetch(
				`/api/projects/${webhook.projectId}/webhooks/${webhook.id}`,
				{
					method: "PATCH",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ enabled: !webhook.enabled }),
				},
			);
			if (!r.ok) throw new Error(`toggle: ${r.status}`);
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["webhooks", webhook.projectId] }),
	});

	const [removeOpen, setRemoveOpen] = useState(false);

	const copyUrl = async () => {
		try {
			await navigator.clipboard.writeText(url);
			setCopied(true);
			setTimeout(() => setCopied(false), 1200);
		} catch {
			// silent
		}
	};

	return (
		<div className="glass rounded-[12px] p-3 space-y-2">
			<div className="flex items-center justify-between gap-2">
				<div className="min-w-0">
					<div className="flex items-center gap-2">
						<span className="text-[13px] truncate">{webhook.name}</span>
						<span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--color-border)] text-[var(--color-text-dim)]">
							{webhook.mode}
						</span>
						{webhook.enabled ? (
							<span className="text-[10px] uppercase tracking-wider text-[var(--color-success)]">
								enabled
							</span>
						) : (
							<span className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
								disabled
							</span>
						)}
					</div>
					{webhook.autoDisabledAt && (
						<div className="text-[11px] text-[var(--color-danger)] mt-0.5">
							auto-disabled: {webhook.autoDisabledReason ?? "too many failures"}
						</div>
					)}
				</div>
				<div className="flex items-center gap-1 shrink-0">
					<Pill onClick={onViewEvents}>View log</Pill>
					<RowMenu
						webhookEnabled={webhook.enabled}
						onEdit={onEdit}
						onToggle={() => toggleEnabled.mutate()}
						onDelete={() => setRemoveOpen(true)}
					/>
				</div>
			</div>
			<div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-black/30 border border-[var(--color-border)] mono text-[11px] text-[var(--color-text-muted)] min-w-0">
				<span className="truncate flex-1">{url}</span>
				<button
					type="button"
					onClick={copyUrl}
					className="text-[var(--color-text-dim)] hover:text-[var(--color-text)] shrink-0"
					aria-label="Copy URL"
				>
					{copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
				</button>
			</div>
			<div className="text-[11px] text-[var(--color-text-muted)] flex items-center gap-3">
				<span>
					source: <span className="mono text-[var(--color-text)]">{webhook.sourceName}</span>
				</span>
				<span className="text-[var(--color-text-dim)]">·</span>
				<span>{webhook.requestCount} requests</span>
				<span className="text-[var(--color-text-dim)]">·</span>
				<span>{webhook.ingestCount} nodes ingested</span>
				{webhook.lastReceivedAt && (
					<>
						<span className="text-[var(--color-text-dim)]">·</span>
						<span>last hit {relativeTime(webhook.lastReceivedAt)}</span>
					</>
				)}
			</div>

			<ConfirmDialog
				open={removeOpen}
				onOpenChange={setRemoveOpen}
				title="Delete webhook?"
				body={
					<>
						Deletes the webhook and its event log. External callers will get 404 after this.
					</>
				}
				confirmLabel="Delete"
				tone="danger"
				onConfirm={() => remove.mutateAsync()}
			/>
		</div>
	);
}

interface RowMenuProps {
	webhookEnabled: boolean;
	onEdit: () => void;
	onToggle: () => void;
	onDelete: () => void;
}

function RowMenu({ webhookEnabled, onEdit, onToggle, onDelete }: RowMenuProps) {
	return (
		<Menu.Root>
			<Menu.Trigger
				className="pill pill-icon !w-8 !h-8 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
				aria-label="More actions"
			>
				<MoreHorizontal className="w-3.5 h-3.5" />
			</Menu.Trigger>
			<Menu.Portal>
				<Menu.Positioner sideOffset={6} align="end" className="z-50">
					<Menu.Popup
						className="rounded-[10px] py-1 min-w-[180px]"
						style={{
							background: "rgb(20 20 23 / 0.98)",
							backdropFilter: "blur(20px) saturate(180%)",
							WebkitBackdropFilter: "blur(20px) saturate(180%)",
							border: "1px solid rgb(255 255 255 / 0.1)",
							boxShadow:
								"0 12px 40px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06)",
						}}
					>
						<Menu.Item
							onClick={onEdit}
							className="flex items-center gap-2 mx-1 my-px px-3 py-1.5 rounded-[6px] text-[13px] outline-none cursor-pointer data-[highlighted]:bg-white/[0.06]"
						>
							<Pencil className="w-3.5 h-3.5 text-[var(--color-text-dim)]" />
							Edit
						</Menu.Item>
						<Menu.Item
							onClick={onToggle}
							className="flex items-center gap-2 mx-1 my-px px-3 py-1.5 rounded-[6px] text-[13px] outline-none cursor-pointer data-[highlighted]:bg-white/[0.06]"
						>
							<Power className="w-3.5 h-3.5 text-[var(--color-text-dim)]" />
							{webhookEnabled ? "Disable" : "Enable"}
						</Menu.Item>
						<div className="border-t border-[var(--color-border)] my-1 mx-1" />
						<Menu.Item
							onClick={onDelete}
							className="flex items-center gap-2 mx-1 my-px px-3 py-1.5 rounded-[6px] text-[13px] text-[var(--color-danger)] outline-none cursor-pointer data-[highlighted]:bg-[rgba(239,68,68,0.08)]"
						>
							<Trash2 className="w-3.5 h-3.5" />
							Delete
						</Menu.Item>
					</Menu.Popup>
				</Menu.Positioner>
			</Menu.Portal>
		</Menu.Root>
	);
}

function relativeTime(iso: string): string {
	const d = new Date(iso);
	const diff = Date.now() - d.getTime();
	if (diff < 0) return "just now";
	const s = Math.round(diff / 1000);
	if (s < 60) return `${s}s ago`;
	const m = Math.round(s / 60);
	if (m < 60) return `${m}m ago`;
	const h = Math.round(m / 60);
	if (h < 24) return `${h}h ago`;
	const days = Math.round(h / 24);
	return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// Repository section: link the project to a GitHub installation + repo +
// branch. The data is stored but no auto-sync runs today — this is
// foundational plumbing.

interface InstallationLite {
	id: string;
	installationId: number;
	accountLogin: string;
	accountType: string;
	repositorySelection: string | null;
	suspendedAt: number | null;
}

interface RepoLite {
	name: string;
	fullName: string;
	private: boolean;
	defaultBranch: string;
}

interface BranchLite {
	name: string;
	commit_sha: string;
}

interface RepositorySectionProps {
	project: Project;
	projectId: string;
}

function RepositorySection({ project, projectId }: RepositorySectionProps) {
	const qc = useQueryClient();
	const linked = project.githubRepo != null && project.githubInstallationId != null;

	const installsQ = useQuery({
		queryKey: ["github-installations"],
		queryFn: async (): Promise<InstallationLite[]> => {
			const r = await fetch("/api/github/installations", { credentials: "include" });
			if (!r.ok) throw new Error(`installs: ${r.status}`);
			return ((await r.json()) as { installations: InstallationLite[] }).installations;
		},
	});

	const unlink = useMutation({
		mutationFn: async () => {
			const r = await fetch(`/api/projects/${projectId}/github/link`, {
				method: "DELETE",
				credentials: "include",
			});
			if (!r.ok) throw new Error(`unlink: ${r.status}`);
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["project", projectId] });
			qc.invalidateQueries({ queryKey: ["projects"] });
		},
	});

	const sync = useMutation({
		mutationFn: async (): Promise<{ jobId: string }> => {
			const r = await fetch(`/api/projects/${projectId}/github/sync`, {
				method: "POST",
				credentials: "include",
			});
			if (!r.ok) throw new Error(`sync: ${r.status}`);
			return (await r.json()) as { jobId: string };
		},
		onSuccess: () => {
			// Re-poll the project a few times so lastBaselineSyncAt
			// updates without a manual refresh.
			const start = Date.now();
			const interval = setInterval(() => {
				qc.invalidateQueries({ queryKey: ["project", projectId] });
				if (Date.now() - start > 60_000) clearInterval(interval);
			}, 3_000);
		},
	});

	const installs = installsQ.data ?? [];
	const noInstalls = !installsQ.isLoading && installs.length === 0;

	return (
		<GlassPanel className="p-5 space-y-4">
			<div className="flex items-start justify-between gap-4">
				<SectionHeader
					title="Repository"
					subtitle="Link this project to a GitHub repository."
				/>
			</div>

			{linked ? (
				<>
					<LinkedRepoRow
						repo={project.githubRepo!}
						branch={project.defaultBranch ?? "main"}
						lastSyncedAt={project.lastBaselineSyncAt ?? null}
						onSync={() => sync.mutate()}
						syncing={sync.isPending}
						onDisconnect={() => unlink.mutate()}
						disconnecting={unlink.isPending}
					/>
					<SyncConfigSection
						projectId={projectId}
						config={project.syncConfig ?? DEFAULT_SYNC_CONFIG}
						defaultBranch={project.defaultBranch ?? "main"}
					/>
				</>
			) : noInstalls ? (
				<div className="text-[13px] text-[var(--color-text-muted)] py-4 text-center border border-dashed border-[var(--color-border)] rounded-[8px]">
					<div>No GitHub accounts connected yet.</div>
					<div className="mt-2">
						<Link
							to="/account/settings"
							className="inline-flex items-center gap-1.5 text-[var(--color-text)] hover:underline"
						>
							<Github className="w-3.5 h-3.5" />
							Connect GitHub in account settings
							<ExternalLink className="w-3 h-3" />
						</Link>
					</div>
				</div>
			) : (
				<LinkPicker
					projectId={projectId}
					installs={installs}
					currentBranch={project.defaultBranch ?? "main"}
				/>
			)}
		</GlassPanel>
	);
}

function LinkedRepoRow({
	repo,
	branch,
	lastSyncedAt,
	onSync,
	syncing,
	onDisconnect,
	disconnecting,
}: {
	repo: string;
	branch: string;
	lastSyncedAt: string | number | null;
	onSync: () => void;
	syncing: boolean;
	onDisconnect: () => void;
	disconnecting: boolean;
}) {
	const syncedLabel = formatLastSynced(lastSyncedAt);
	return (
		<div className="flex items-center justify-between gap-4 py-1">
			<div className="flex items-center gap-3 min-w-0">
				<Github className="w-4 h-4 text-[var(--color-text-muted)] shrink-0" />
				<div className="min-w-0">
					<div className="text-[13px] truncate font-medium">{repo}</div>
					<div className="text-[11px] text-[var(--color-text-dim)] mt-0.5">
						<span className="mono">{branch}</span>
						<span className="mx-1.5">·</span>
						{syncedLabel}
					</div>
				</div>
			</div>
			<div className="flex items-center gap-1">
				<TextButton onClick={onSync} disabled={syncing}>
					{syncing ? (
						<>
							<Loader2 className="w-3 h-3 animate-spin" />
							Syncing
						</>
					) : (
						<>
							<RefreshCw className="w-3 h-3" />
							Sync
						</>
					)}
				</TextButton>
				<TextButton onClick={onDisconnect} disabled={disconnecting} subtle>
					{disconnecting ? (
						<Loader2 className="w-3 h-3 animate-spin" />
					) : (
						<Link2Off className="w-3 h-3" />
					)}
					Disconnect
				</TextButton>
			</div>
		</div>
	);
}

function TextButton({
	children,
	onClick,
	disabled,
	subtle,
}: {
	children: ReactNode;
	onClick: () => void;
	disabled?: boolean;
	subtle?: boolean;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className={`inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[12px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/15 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/[0.04] ${
				subtle
					? "text-[var(--color-text-dim)] hover:text-[var(--color-text-muted)]"
					: "text-[var(--color-text)]"
			}`}
		>
			{children}
		</button>
	);
}

function formatLastSynced(at: string | number | null): string {
	if (at == null) return "Never synced";
	const t = typeof at === "string" ? new Date(at).getTime() : at;
	if (Number.isNaN(t)) return "Never synced";
	const diffMs = Date.now() - t;
	if (diffMs < 60_000) return "Synced just now";
	const minutes = Math.floor(diffMs / 60_000);
	if (minutes < 60) return `Synced ${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `Synced ${hours}h ago`;
	const days = Math.floor(hours / 24);
	return `Synced ${days}d ago`;
}

const SLICE_LABELS: { key: keyof SyncSlices; label: string; hint: string }[] = [
	{ key: "docs", label: "Docs", hint: "*.md files" },
	{ key: "commits", label: "Commits", hint: "commit messages + DAG" },
	{ key: "releases", label: "Releases", hint: "release notes" },
	{ key: "prs", label: "Pull requests", hint: "PRs + review threads" },
	{ key: "issues", label: "Issues", hint: "issues + comments" },
	{ key: "code", label: "Code", hint: "TS/JS source — higher cost" },
];

function SyncConfigSection({
	projectId,
	config,
	defaultBranch,
}: {
	projectId: string;
	config: SyncConfig;
	defaultBranch: string;
}) {
	const qc = useQueryClient();
	const [draft, setDraft] = useState<SyncConfig>(config);
	const [branchInput, setBranchInput] = useState(config.branch ?? "");

	useEffect(() => {
		setDraft(config);
		setBranchInput(config.branch ?? "");
	}, [config]);

	const save = useMutation({
		mutationFn: async (next: SyncConfig) => {
			const r = await fetch(
				`/api/projects/${projectId}/github/sync-config`,
				{
					method: "PATCH",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(next),
				},
			);
			if (!r.ok) throw new Error(`sync-config: ${r.status}`);
			return (await r.json()) as { syncConfig: SyncConfig };
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["project", projectId] });
		},
	});

	function toggleSlice(key: keyof SyncSlices) {
		const next: SyncConfig = {
			...draft,
			slices: { ...draft.slices, [key]: !draft.slices[key] },
		};
		setDraft(next);
		save.mutate(next);
	}

	function commitBranch(value: string) {
		const trimmed = value.trim();
		const nextBranch = trimmed ? trimmed : undefined;
		if (nextBranch === draft.branch) return;
		const next: SyncConfig = { ...draft, branch: nextBranch };
		setDraft(next);
		save.mutate(next);
	}

	return (
		<div className="space-y-6 pt-2">
			{/* Branch override */}
			<SettingRow
				label="Sync branch"
				hint={
					draft.branch
						? `Sync follows ${draft.branch} instead of the linked branch ${defaultBranch}.`
						: `Sync follows the linked branch (${defaultBranch}). Set a different branch here to override.`
				}
			>
				<input
					type="text"
					value={branchInput}
					onChange={(e) => setBranchInput(e.target.value)}
					onBlur={() => commitBranch(branchInput)}
					onKeyDown={(e) => {
						if (e.key === "Enter") (e.target as HTMLInputElement).blur();
					}}
					placeholder={defaultBranch}
					disabled={save.isPending}
					className="
						h-8 w-[180px] px-2.5 rounded-md text-[12px] mono
						bg-[var(--color-surface-solid)]
						border border-[var(--color-border)]
						text-[var(--color-text)]
						placeholder:text-[var(--color-text-dim)]
						focus:outline-none focus:border-[var(--color-border-hi)]
						focus-visible:ring-2 focus-visible:ring-white/15
						disabled:opacity-50
					"
				/>
			</SettingRow>

			{/* What to sync */}
			<div className="space-y-1">
				<div className="text-[13px] font-medium">What to sync</div>
				<div className="text-[11px] text-[var(--color-text-dim)]">
					Each switch is a kind of content the graph picks up.
				</div>
			</div>

			<div className="space-y-px">
				{SLICE_LABELS.map((s) => (
					<SettingRow key={s.key} label={s.label} hint={s.hint}>
						<Switch
							checked={draft.slices[s.key]}
							onCheckedChange={() => toggleSlice(s.key)}
							disabled={save.isPending}
							aria-label={s.label}
						/>
					</SettingRow>
				))}
				<div className="pt-3 mt-2 border-t border-[var(--color-border)]">
					<SettingRow
						label="Include bot activity"
						hint="Ingest PRs and comments authored by Dependabot, Renovate, etc."
					>
						<Switch
							checked={draft.includeBots === true}
							onCheckedChange={() => {
								const next: SyncConfig = {
									...draft,
									includeBots: !draft.includeBots,
								};
								setDraft(next);
								save.mutate(next);
							}}
							disabled={save.isPending}
							aria-label="Include bot activity"
						/>
					</SettingRow>
				</div>
			</div>

			{/* PR review agent */}
			<div className="pt-6 mt-4 border-t border-[var(--color-border)] space-y-3">
				<div className="space-y-1">
					<div className="text-[13px] font-medium">
						PR review{" "}
						<span className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] ml-1">
							beta
						</span>
					</div>
					<div className="text-[11px] text-[var(--color-text-dim)]">
						When enabled, adding the{" "}
						<code className="mono text-[10px] px-1 py-px rounded bg-white/[0.06] text-[var(--color-text-muted)]">
							renketsu-review
						</code>{" "}
						label to any PR on the linked repo triggers an agent that reads the diff, queries this project's graph (issues, docs, past PRs), and posts a review citing only the cross-source contradictions it can find. Saying nothing is the default if no contradictions are flagged.
					</div>
				</div>
				<SettingRow
					label="Enable PR reviews"
					hint="Off by default. Each posted review counts against the pr_reviews quota."
				>
					<Switch
						checked={draft.prReviews === true}
						onCheckedChange={() => {
							const next: SyncConfig = {
								...draft,
								prReviews: !draft.prReviews,
							};
							setDraft(next);
							save.mutate(next);
						}}
						disabled={save.isPending}
						aria-label="Enable PR reviews"
					/>
				</SettingRow>
			</div>
		</div>
	);
}

function SettingRow({
	label,
	hint,
	children,
}: {
	label: string;
	hint?: string;
	children: ReactNode;
}) {
	return (
		<div className="flex items-center justify-between gap-4 py-2">
			<div className="min-w-0">
				<div className="text-[12px] text-[var(--color-text)]">{label}</div>
				{hint && (
					<div className="text-[11px] text-[var(--color-text-dim)] mt-0.5">
						{hint}
					</div>
				)}
			</div>
			<div className="shrink-0">{children}</div>
		</div>
	);
}

function LinkPicker({
	projectId,
	installs,
	currentBranch,
}: {
	projectId: string;
	installs: InstallationLite[];
	currentBranch: string;
}) {
	const qc = useQueryClient();
	const [installId, setInstallId] = useState<string>(installs[0]?.id ?? "");
	const [repoFullName, setRepoFullName] = useState<string>("");
	const [branch, setBranch] = useState<string>(currentBranch);

	const reposQ = useQuery({
		queryKey: ["github-repos", installId],
		queryFn: async (): Promise<RepoLite[]> => {
			const r = await fetch(
				`/api/github/installations/${encodeURIComponent(installId)}/repos`,
				{ credentials: "include" },
			);
			if (!r.ok) throw new Error(`repos: ${r.status}`);
			return ((await r.json()) as { repos: RepoLite[] }).repos;
		},
		enabled: !!installId,
	});

	const branchesQ = useQuery({
		queryKey: ["github-branches", installId, repoFullName],
		queryFn: async (): Promise<BranchLite[]> => {
			const [owner, name] = repoFullName.split("/");
			const r = await fetch(
				`/api/github/installations/${encodeURIComponent(installId)}/repos/${encodeURIComponent(owner!)}/${encodeURIComponent(name!)}/branches`,
				{ credentials: "include" },
			);
			if (!r.ok) throw new Error(`branches: ${r.status}`);
			return ((await r.json()) as { branches: BranchLite[] }).branches;
		},
		enabled: !!installId && !!repoFullName,
	});

	// When the user picks a repo, default the branch to that repo's default.
	useEffect(() => {
		const repo = reposQ.data?.find((r) => r.fullName === repoFullName);
		if (repo) setBranch(repo.defaultBranch);
	}, [repoFullName, reposQ.data]);

	const link = useMutation({
		mutationFn: async () => {
			const r = await fetch(`/api/projects/${projectId}/github/link`, {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					installationId: installId,
					repo: repoFullName,
					branch,
				}),
			});
			if (!r.ok) throw new Error(`link: ${r.status}`);
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["project", projectId] });
			qc.invalidateQueries({ queryKey: ["projects"] });
		},
	});

	const canSave = !!installId && !!repoFullName && !!branch && !link.isPending;

	return (
		<div className="space-y-3">
			<Field label="GitHub account">
				<GlassSelect
					value={installId}
					onValueChange={(v) => {
						setInstallId(v);
						setRepoFullName("");
					}}
					placeholder="Select an account"
					items={installs.map((i) => ({
						value: i.id,
						label: i.accountLogin,
						hint: i.accountType.toLowerCase(),
					}))}
				/>
			</Field>

			<Field label="Repository">
				<GlassSelect
					value={repoFullName}
					onValueChange={setRepoFullName}
					placeholder={
						reposQ.isLoading ? "Loading repos…" : "Select a repository"
					}
					disabled={reposQ.isLoading || !installId}
					mono
					items={(reposQ.data ?? []).map((r) => ({
						value: r.fullName,
						label: r.fullName,
						hint: r.private ? "private" : undefined,
					}))}
				/>
			</Field>

			<Field label="Branch">
				<GlassSelect
					value={branch}
					onValueChange={setBranch}
					placeholder={
						branchesQ.isLoading ? "Loading branches…" : "Select a branch"
					}
					disabled={branchesQ.isLoading || !repoFullName}
					mono
					items={(branchesQ.data ?? []).map((b) => ({
						value: b.name,
						label: b.name,
					}))}
				/>
			</Field>

			<div className="flex items-center justify-between pt-1">
				<div className="text-[11px] text-[var(--color-text-dim)]">
					Stored on the project; auto-sync is a future feature.
				</div>
				<Pill onClick={() => link.mutate()} disabled={!canSave}>
					{link.isPending ? (
						<Loader2 className="w-3.5 h-3.5 animate-spin" />
					) : (
						<Link2 className="w-3.5 h-3.5" />
					)}
					Connect
				</Pill>
			</div>
		</div>
	);
}
